/**
 * Frontend-only TaskRegistry fixture.
 *
 * Mirrors the shape of the GraphQL `WorkflowTaskDefinition` type from the
 * primitives implementation plan. When the backend resolvers land, swap the
 * default export for the `workflowTaskDefinitions` query result; nothing
 * else in the builder should need to change.
 */

import type {
  InputSubtypeDefinition,
  PrimitiveDefinition,
  TaskDefinitionKey
} from '~/pages/workflows/builder/types';

export const INPUT_SUBTYPES: InputSubtypeDefinition[] = [
  {
    key: 'document',
    taskKey: 'INPUT_DOCUMENT',
    label: 'Document',
    description: 'PDFs, images, Word docs, and other unstructured files',
    summaryTemplate: '{label} ({mime})',
    needsSlot: true,
    defaultSlotLabel: 'Documents',
    slotKind: 'FILE',
    acceptedMimeTypes: [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/tiff',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]
  },
  {
    key: 'spreadsheet',
    taskKey: 'INPUT_SPREADSHEET',
    label: 'Spreadsheet',
    description: 'Excel or CSV file parsed into rows of data',
    summaryTemplate: '{label} · rows',
    needsSlot: true,
    defaultSlotLabel: 'Spreadsheet',
    slotKind: 'FILE',
    acceptedMimeTypes: [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ]
  },
  {
    key: 'text',
    taskKey: 'INPUT_TEXT',
    label: 'Text',
    description: 'A free-form text value (email body, notes, a single field)',
    summaryTemplate: '{label}',
    needsSlot: true,
    defaultSlotLabel: 'Text',
    slotKind: 'TEXT'
  },
  {
    key: 'json',
    taskKey: 'INPUT_JSON',
    label: 'JSON',
    description: 'A structured JSON value, optionally schema-validated',
    summaryTemplate: '{label}',
    needsSlot: true,
    defaultSlotLabel: 'Data',
    slotKind: 'JSON'
  },
  {
    key: 'ams',
    taskKey: 'INPUT_AMS_READ',
    label: 'AMS record',
    description: 'Account, policy, driver, vehicle, or location records from the AMS',
    summaryTemplate: '{label} · AMS',
    needsSlot: true,
    defaultSlotLabel: 'AMS record',
    slotKind: 'AMS_SELECTION'
  },
  {
    key: 'web',
    taskKey: 'INPUT_WEB_LOOKUP',
    label: 'Web lookup',
    description: 'Fetches live data from the web based on a query',
    summaryTemplate: 'Web lookup',
    needsSlot: false,
    defaultSlotLabel: ''
  }
];

export const SUBTYPE_BY_TASK: Record<string, InputSubtypeDefinition | undefined> =
  Object.fromEntries(INPUT_SUBTYPES.map((s) => [s.taskKey, s]));

// ----- Shared descriptors -----

const OCR_MODE_OPTIONS = [
  { value: 'auto', label: 'Auto (detect)' },
  { value: 'force', label: 'Always OCR' },
  { value: 'off', label: 'Skip OCR' }
];

const EXTRACT_ENTITY_KINDS = [
  { value: 'drivers', label: 'Drivers' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'locations', label: 'Locations' },
  { value: 'limits', label: 'Limits' },
  { value: 'forms', label: 'Forms' },
  { value: 'endorsements', label: 'Endorsements' },
  { value: 'cope', label: 'COPE' },
  { value: 'coverage', label: 'Coverage' },
  { value: 'exclusions', label: 'Exclusions' },
  { value: 'policy_metadata', label: 'Policy metadata' }
];

export const EXTRACT_BUILTIN_ENTITIES = EXTRACT_ENTITY_KINDS;

// ----- The 12 primitives -----

export const PRIMITIVES: PrimitiveDefinition[] = [
  {
    key: 'INPUT_DOCUMENT',
    label: 'Document',
    shortLabel: 'Document',
    description: 'Processes uploaded PDFs, images, and Word documents',
    category: 'input',
    inputSchema: [
      {
        key: 'ocrMode',
        label: 'OCR mode',
        description: 'Whether to run OCR on scanned documents',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        default: 'auto',
        options: OCR_MODE_OPTIONS
      },
      {
        key: 'maxPages',
        label: 'Max pages (optional)',
        description: 'Cap the number of pages processed per file',
        fieldKind: 'CONFIG',
        valueType: 'number',
        isList: false,
        required: false
      }
    ],
    outputSchema: [
      {
        key: 'file',
        label: 'File',
        description: 'The uploaded file (passthrough)',
        valueType: 'file',
        isList: false
      },
      {
        key: 'text',
        label: 'Full text',
        description: 'OCR-ed text of the document',
        valueType: 'text',
        isList: false
      },
      {
        key: 'pages',
        label: 'Pages',
        description: 'Per-page text chunks',
        valueType: 'text',
        isList: true
      }
    ]
  },
  {
    key: 'INPUT_SPREADSHEET',
    label: 'Spreadsheet',
    shortLabel: 'Spreadsheet',
    description: 'Parses Excel or CSV into rows',
    category: 'input',
    inputSchema: [
      {
        key: 'headerRow',
        label: 'Header row',
        description: '0-indexed row that contains column names. Use -1 if no header.',
        fieldKind: 'CONFIG',
        valueType: 'number',
        isList: false,
        required: false,
        default: 0
      },
      {
        key: 'sheetName',
        label: 'Sheet name',
        description: 'For Excel workbooks with multiple sheets',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false
      },
      {
        key: 'trimWhitespace',
        label: 'Trim whitespace',
        description: 'Remove leading/trailing whitespace in every cell',
        fieldKind: 'CONFIG',
        valueType: 'bool',
        isList: false,
        required: false,
        default: true
      }
    ],
    outputSchema: [
      {
        key: 'rows',
        label: 'Rows',
        description: 'Each row as a dictionary keyed by column name',
        valueType: 'record',
        isList: true
      },
      {
        key: 'headers',
        label: 'Headers',
        description: 'List of column names',
        valueType: 'text',
        isList: true
      }
    ]
  },
  {
    key: 'INPUT_TEXT',
    label: 'Text',
    shortLabel: 'Text',
    description: 'Passes through (and optionally splits) a text value',
    category: 'input',
    inputSchema: [
      {
        key: 'trim',
        label: 'Trim whitespace',
        description: 'Remove leading/trailing whitespace',
        fieldKind: 'CONFIG',
        valueType: 'bool',
        isList: false,
        required: false,
        default: true
      },
      {
        key: 'splitOn',
        label: 'Split on',
        description: 'Optional separator to split the text into lines (e.g. "\\n")',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false
      }
    ],
    outputSchema: [
      {
        key: 'text',
        label: 'Text',
        description: 'The original text',
        valueType: 'text',
        isList: false
      },
      {
        key: 'lines',
        label: 'Lines',
        description: 'Split lines (if splitOn was provided)',
        valueType: 'text',
        isList: true
      }
    ]
  },
  {
    key: 'INPUT_JSON',
    label: 'JSON',
    shortLabel: 'JSON',
    description: 'Validates and passes through a structured value',
    category: 'input',
    inputSchema: [
      {
        key: 'schema',
        label: 'JSON Schema',
        description: 'Optional JSON Schema to validate the value against',
        fieldKind: 'CONFIG',
        valueType: 'json',
        isList: false,
        required: false,
        multiline: true
      }
    ],
    outputSchema: [
      {
        key: 'value',
        label: 'Value',
        description: 'The parsed JSON value',
        valueType: 'json',
        isList: false
      }
    ]
  },
  {
    key: 'INPUT_AMS_READ',
    label: 'AMS record',
    shortLabel: 'AMS',
    description: 'Pulls structured records from the connected AMS',
    category: 'input',
    inputSchema: [
      {
        key: 'entities',
        label: 'Records to fetch',
        description: 'Which record types to pull for this account',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: true,
        required: true,
        default: ['account'],
        chipInput: true,
        options: [
          { value: 'account', label: 'Account' },
          { value: 'policies', label: 'Policies' },
          { value: 'drivers', label: 'Drivers' },
          { value: 'vehicles', label: 'Vehicles' },
          { value: 'locations', label: 'Locations' }
        ]
      }
    ],
    outputSchema: [
      {
        key: 'account',
        label: 'Account',
        description: 'The account record',
        valueType: 'record',
        isList: false
      },
      {
        key: 'policies',
        label: 'Policies',
        description: 'Policy records',
        valueType: 'record',
        isList: true
      },
      {
        key: 'drivers',
        label: 'Drivers',
        description: 'Driver records',
        valueType: 'record',
        isList: true
      },
      {
        key: 'vehicles',
        label: 'Vehicles',
        description: 'Vehicle records',
        valueType: 'record',
        isList: true
      },
      {
        key: 'locations',
        label: 'Locations',
        description: 'Location records',
        valueType: 'record',
        isList: true
      }
    ]
  },
  {
    key: 'INPUT_WEB_LOOKUP',
    label: 'Web lookup',
    shortLabel: 'Web lookup',
    description: 'Searches the web for information',
    category: 'input',
    inputSchema: [
      {
        key: 'query',
        label: 'Query',
        description: 'The search query (usually bound to an upstream value)',
        fieldKind: 'INPUT',
        valueType: 'text',
        acceptedKinds: ['text'],
        isList: false,
        required: true
      },
      {
        key: 'siteHints',
        label: 'Site hints',
        description: 'Optional list of domains to prefer (e.g. "fema.gov")',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: true,
        required: false,
        chipInput: true
      },
      {
        key: 'maxResults',
        label: 'Max results',
        description: 'Maximum number of results to return',
        fieldKind: 'CONFIG',
        valueType: 'number',
        isList: false,
        required: false,
        default: 5
      }
    ],
    outputSchema: [
      {
        key: 'results',
        label: 'Results',
        description: 'Search results (title, url, snippet)',
        valueType: 'record',
        isList: true
      }
    ]
  },
  // ----- Analysis -----
  {
    key: 'EXTRACT',
    label: 'Extract',
    shortLabel: 'Extract',
    description: 'Pulls structured data from documents (drivers, vehicles, locations, custom)',
    category: 'analysis',
    inputSchema: [
      {
        // Matches the backend's ExtractIn.Source field (wf:"input,accepts=file|json|text")
        key: 'source',
        label: 'Source',
        description: 'Document, JSON payload, or text to extract from',
        fieldKind: 'INPUT',
        valueType: 'any',
        acceptedKinds: ['file', 'json', 'text'],
        isList: false,
        required: true
      }
      // NB: the `entities` list lives in node.config.entities (array of
      // EntitySpec) and is rendered by a bespoke ExtractInspector — not a
      // standard schema field, because each entity becomes a virtual output
      // port on the node.
    ],
    outputSchema: [
      {
        key: 'entities',
        label: 'Entities',
        description: 'Extracted entity map (keyed by entity output_key)',
        valueType: 'json',
        isList: false
      }
      // Dynamic virtual ports (one per configured entity) are appended by
      // selectors.ts based on node.config.entities.
    ]
  },
  {
    key: 'AGGREGATE',
    label: 'Aggregate',
    shortLabel: 'Aggregate',
    description: 'Combines multiple upstream results into one',
    category: 'analysis',
    inputSchema: [
      {
        // Matches the backend's AggregateIn.Values field (wf:"input,required,list")
        key: 'values',
        label: 'Inputs to combine',
        description: 'One or more upstream values or lists',
        fieldKind: 'INPUT',
        valueType: 'any',
        acceptsMany: true,
        isList: true,
        required: true
      },
      {
        key: 'strategy',
        label: 'Strategy',
        description: 'How to combine the inputs',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        default: 'concat',
        options: [
          {
            value: 'concat',
            label: 'Concatenate',
            description: 'Flatten all inputs into a single list'
          },
          { value: 'dedupe', label: 'Dedupe', description: 'Remove duplicates by a key' },
          {
            value: 'merge_object',
            label: 'Merge objects',
            description: 'Deep-merge matching objects'
          },
          {
            value: 'llm',
            label: 'LLM synthesis',
            description: 'Use an LLM to summarize or reconcile'
          }
        ]
      },
      {
        key: 'keyPath',
        label: 'Key path',
        description: 'Dotted path used to group items (e.g. "id")',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        when: { field: 'strategy', equals: ['dedupe', 'merge_object'] }
      },
      {
        key: 'sortPath',
        label: 'Sort path',
        description: 'Dotted path used to sort the result',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        when: { field: 'strategy', equals: ['dedupe', 'merge_object'] }
      },
      {
        key: 'prompt',
        label: 'Prompt',
        description: 'Instructions for the LLM',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        multiline: true,
        when: { field: 'strategy', equals: ['llm'] }
      },
      {
        key: 'schema',
        label: 'Output schema',
        description: 'Optional JSON Schema to constrain the LLM output',
        fieldKind: 'CONFIG',
        valueType: 'json',
        isList: false,
        required: false,
        multiline: true,
        when: { field: 'strategy', equals: ['llm'] }
      }
    ],
    outputSchema: [
      {
        key: 'result',
        label: 'Result',
        description: 'The aggregated value',
        valueType: 'json',
        isList: false
      }
    ]
  },
  {
    key: 'COMPARE',
    label: 'Compare',
    shortLabel: 'Compare',
    description: 'Finds matches, gaps, and discrepancies between two data sets',
    category: 'analysis',
    inputSchema: [
      {
        key: 'left',
        label: 'Left side',
        description: 'First data set',
        fieldKind: 'INPUT',
        valueType: 'any',
        isList: false,
        required: true
      },
      {
        key: 'right',
        label: 'Right side',
        description: 'Second data set',
        fieldKind: 'INPUT',
        valueType: 'any',
        isList: false,
        required: true
      },
      {
        key: 'joinType',
        label: 'Join type',
        description: "Which side's items appear in the result",
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        default: 'full',
        options: [
          { value: 'full', label: 'Full' },
          { value: 'left', label: 'Left' }
        ]
      },
      {
        key: 'joinKey',
        label: 'Join key',
        description: 'Dotted path used to match items across the two sides',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false
      },
      {
        key: 'prompt',
        label: 'Comparison prompt',
        description: 'Optional instructions for the semantic comparison',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        multiline: true
      }
    ],
    outputSchema: [
      {
        key: 'differences',
        label: 'Differences',
        description: 'Matched, unmatched, and field-level discrepancies',
        valueType: 'record',
        isList: false
      }
    ]
  },
  {
    key: 'VERIFY',
    label: 'Verify',
    shortLabel: 'Verify',
    description: 'Confirms data against a reference source (AMS, document, web)',
    category: 'analysis',
    inputSchema: [
      {
        // Matches the backend's VerifyIn.Value field (wf:"input,required")
        key: 'value',
        label: 'Value to verify',
        description: 'The data point under review',
        fieldKind: 'INPUT',
        valueType: 'any',
        isList: false,
        required: true
      },
      {
        key: 'criteria',
        label: 'Criteria',
        description: 'What to check on each item',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: true,
        required: false,
        chipInput: true
      },
      {
        key: 'mode',
        label: 'Reference source',
        description: 'Where the ground truth comes from',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        default: 'document',
        options: [
          {
            value: 'document',
            label: 'Document',
            description: 'Compare against a reference document'
          },
          { value: 'ams', label: 'AMS record', description: 'Compare against an AMS record' },
          { value: 'web', label: 'Web search', description: 'Verify against public web data' },
          { value: 'auto', label: 'Auto', description: 'Let the system pick based on input' }
        ]
      },
      {
        key: 'reference',
        label: 'Reference',
        description: 'The ground-truth data source',
        fieldKind: 'INPUT',
        valueType: 'any',
        isList: false,
        required: false,
        when: { field: 'mode', equals: ['document', 'ams'] }
      },
      {
        key: 'reference_query',
        label: 'Reference query',
        description: 'Query string for the web lookup',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        when: { field: 'mode', equals: ['web'] }
      }
    ],
    outputSchema: [
      {
        key: 'passed',
        label: 'Passed',
        description: 'Whether the verification passed',
        valueType: 'bool',
        isList: false
      },
      {
        key: 'reasons',
        label: 'Reasons',
        description: 'Explanation for the verification result',
        valueType: 'text',
        isList: true
      },
      {
        key: 'details',
        label: 'Details',
        description: 'Full verification details',
        valueType: 'record',
        isList: false
      }
    ]
  },
  // ----- Output -----
  {
    key: 'OUTPUT_DOCUMENT',
    label: 'Document output',
    shortLabel: 'Document',
    description: 'Renders a PDF, Word, Excel, or CSV file',
    category: 'output',
    inputSchema: [
      {
        key: 'runMode',
        label: 'Run mode',
        description:
          'Combine an upstream list into a single file, or generate a file per list item',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        default: 'batch',
        options: [
          {
            value: 'batch',
            label: 'Combine into one file',
            description: 'Fan-in: takes the whole list and produces a single file'
          },
          {
            value: 'per-item',
            label: 'One file per item',
            description: 'Fan-out continues: iterates the list and produces one file per item'
          }
        ]
      },
      {
        // Matches backend OutputDocumentIn.HTML (wf:"input,accepts=text") —
        // used by pdf/docx formats.
        key: 'html',
        label: 'HTML content',
        description: 'HTML body to render as pdf/docx',
        fieldKind: 'INPUT',
        valueType: 'text',
        acceptedKinds: ['text', 'json', 'record'],
        isList: false,
        required: false,
        when: { field: 'format', equals: ['pdf', 'docx'] }
      },
      {
        // Matches backend OutputDocumentIn.Rows (wf:"input,list") —
        // used by xlsx/csv formats.
        key: 'rows',
        label: 'Rows',
        description: 'Tabular rows for xlsx/csv output',
        fieldKind: 'INPUT',
        valueType: 'record',
        acceptsMany: true,
        isList: true,
        required: false,
        when: { field: 'format', equals: ['xlsx', 'csv'] }
      },
      {
        key: 'format',
        label: 'Format',
        description: 'Output file format',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        default: 'pdf',
        options: [
          { value: 'pdf', label: 'PDF' },
          { value: 'docx', label: 'Word (.docx)' },
          { value: 'xlsx', label: 'Excel (.xlsx)' },
          { value: 'csv', label: 'CSV' }
        ]
      },
      {
        key: 'title',
        label: 'Title',
        description: 'Default filename / heading',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false
      },
      {
        key: 'template_file',
        label: 'Template file',
        description: 'Optional template used to render the output',
        fieldKind: 'INPUT',
        valueType: 'file',
        acceptedKinds: ['file'],
        isList: false,
        required: false,
        when: { field: 'format', equals: ['pdf', 'docx'] }
      },
      {
        key: 'prompt',
        label: 'Prompt',
        description: 'Instructions used when no template is provided',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: false,
        multiline: true,
        when: { field: 'format', equals: ['pdf', 'docx'] }
      }
      // `columns` for xlsx/csv lives in node.config.columns and is rendered
      // by a bespoke DocumentInspector.
    ],
    outputSchema: [
      {
        key: 'file',
        label: 'File',
        description: 'The generated file',
        valueType: 'file',
        isList: false,
        isArtifact: true
      }
    ]
  },
  {
    key: 'OUTPUT_INTEGRATION',
    label: 'Integration output',
    shortLabel: 'Integration',
    description: 'Writes data back to an AMS, CRM, or other integration',
    category: 'output',
    inputSchema: [
      {
        key: 'runMode',
        label: 'Run mode',
        description:
          'Make one set of calls for the whole list, or run the integration per list item',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true,
        default: 'batch',
        options: [
          {
            value: 'batch',
            label: 'One run for the whole list',
            description: 'Fan-in: each action receives the whole list once'
          },
          {
            value: 'per-item',
            label: 'One run per item',
            description: 'Fan-out continues: runs every action once per upstream list item'
          }
        ]
      },
      {
        // Matches backend OutputIntegrationIn.Payload (wf:"data")
        key: 'payload',
        label: 'Payload',
        description: 'Payload for the actions (can be omitted if each action is fully configured)',
        fieldKind: 'INPUT',
        valueType: 'any',
        isList: false,
        required: false
      },
      {
        key: 'integrationType',
        label: 'Integration',
        description: 'Which system to write to',
        fieldKind: 'CONFIG',
        valueType: 'text',
        isList: false,
        required: true
      }
      // `actions` lives in node.config.actions (list of {capabilityKey, config, dataPath})
      // rendered by a bespoke IntegrationInspector.
    ],
    outputSchema: [
      {
        key: 'results',
        label: 'Results',
        description: 'Per-action status and external IDs',
        valueType: 'record',
        isList: true
      }
    ]
  }
];

export const PRIMITIVE_BY_KEY: Record<TaskDefinitionKey, PrimitiveDefinition> = Object.fromEntries(
  PRIMITIVES.map((p) => [p.key, p])
) as Record<TaskDefinitionKey, PrimitiveDefinition>;

export function getPrimitive(key: TaskDefinitionKey): PrimitiveDefinition {
  const p = PRIMITIVE_BY_KEY[key];
  if (!p) {
    throw new Error(`Unknown task definition key: ${key}`);
  }
  return p;
}
